import React from 'react';
import PropTypes from 'prop-types';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Registrar componentes de Chart.js
if (ChartJS.register) {
  ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
  );
}

const TimeLineChart = ({ procesos = [] }) => {
  // Función para formatear tiempo de manera legible
  const formatTime = (hours) => {
    if (typeof hours !== 'number' || hours < 0) return 'N/A';
    
    if (hours === 0) {
      return '0 min';
    } else if (hours < 1) {
      // Menos de 1 hora: mostrar en minutos
      const minutes = Math.round(hours * 60);
      return `${minutes} min`;
    } else if (hours < 24) {
      // Menos de 24 horas: mostrar horas con minutos
      const totalMinutes = Math.round(hours * 60);
      const hoursPart = Math.floor(totalMinutes / 60);
      const minutesPart = totalMinutes % 60;
      
      if (minutesPart === 0) {
        return hoursPart === 1 ? '1 hora' : `${hoursPart} horas`;
      } else {
        return `${hoursPart}h ${minutesPart}min`;
      }
    } else {
      // 24 horas o más: mostrar días, horas y minutos
      const totalMinutes = Math.round(hours * 60);
      const days = Math.floor(totalMinutes / (24 * 60));
      const remainingMinutes = totalMinutes % (24 * 60);
      const hoursPart = Math.floor(remainingMinutes / 60);
      const minutesPart = remainingMinutes % 60;
      
      let result = '';
      if (days > 0) {
        result += days === 1 ? '1 día' : `${days} días`;
      }
      if (hoursPart > 0) {
        if (result) result += ', ';
        result += hoursPart === 1 ? '1 hora' : `${hoursPart} horas`;
      }
      if (minutesPart > 0) {
        if (result) result += ', ';
        result += `${minutesPart} min`;
      }
      
      return result || '0 min';
    }
  };

  // Validar que procesos sea un array válido
  const procesosValidos = Array.isArray(procesos) ? procesos : [];

  // Preparar datos para el gráfico
  const chartData = {
    labels: procesosValidos.map(proceso => proceso.nombre || 'Proceso sin nombre'),
    datasets: [
      {
        label: 'Tiempo',
        data: procesosValidos.map(proceso => proceso.tiempo_proceso || 0),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.1,
        fill: false,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Timeline de Procesos',
        font: {
          size: 16,
          weight: 'bold',
        },
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const proceso = procesos[context.dataIndex];
            const usuario = proceso?.usuario || proceso?.proveedor || 'No asignado';
            return `${context.dataset.label}: ${context.parsed.y} horas (${usuario})`;
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Procesos',
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0,
        },
      },
      y: {
        title: {
          display: true,
          text: 'Tiempo',
        },
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return formatTime(value);
          },
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
  };

  // Si no hay procesos, mostrar mensaje
  if (!procesosValidos || procesosValidos.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ height: '300px' }}>
        <div className="text-center">
          <i className="fas fa-chart-line fa-3x text-muted mb-3"></i>
          <p className="text-muted">No hay datos de procesos para mostrar</p>
        </div>
      </div>
    );
  }

  return (
    <div className="timeline-chart">
      <Line data={chartData} options={options} />
    </div>
  );
};

TimeLineChart.propTypes = {
  procesos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      nombre: PropTypes.string,
      fecha_inicio: PropTypes.string,
      fecha_fin: PropTypes.string,
      tiempo_proceso: PropTypes.number,
      usuario: PropTypes.string,
      proveedor: PropTypes.string,
    })
  ),
};

export default TimeLineChart;
